import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LrGrid from 'components/LrGrid';
import { Card, CardContent, IconButton, Paper, Typography } from '@mui/material';
import { defaultColDef, getBrLocationReviewColumnDefs } from './BrLocationReviewGridColumnDefs';
import BrLocationReviewDetailRenderer from './BrLocationReviewDetailRenderer';
import { Location } from 'types/location.type';
import { useSelector } from 'react-redux';
import * as Actions from 'application/redux/actions';
import {
    getSovLocations,
    getSubmissionDetails,
    getUwBRPropertyValues,
    getUwBRReview,
    getUwReviewsData,
} from 'application/redux/selectors';
import { getProductFilteredLocations } from 'helpers/submissions';
import { COLORS, PRODUCT_LINE_TYPES } from 'constants/index';
import BRPropertyModel from './BRPropertyModel';
import { useActions } from 'hooks/useActions';
import RestoreUnavailableLocationDialog from 'components/Underwriting/components/RestoreUnavailableLocationDialog';
import { restoreLocations } from '../../helpers';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import {
    getBrPremiumSubtotal,
    getReviewLumenParticipationPercent,
    updateDerivedFieldsForBrLocationReview,
} from 'helpers/brCalculationHelper';
import { SubmissionDetails } from 'types/submission.type';
import { GetRowIdParams } from 'ag-grid-enterprise';

const MY_PRODUCT_LINE_TYPE: string = PRODUCT_LINE_TYPES.BUILDERS_RISK;

interface Props {
    underwriterReviewId?: string | null;
    readOnly?: boolean;
}

const buildRowData = (locations: Location[], submission: SubmissionDetails, propertyReviews: BRPropertyModel[]) => {
    const rows: any = getProductFilteredLocations(MY_PRODUCT_LINE_TYPE, locations, submission).filter(
        (l) => !l.markedAsDeleted
    );
    rows.forEach((row: any) => {
        const existingReview = propertyReviews.find((p) => p && p.locationId === row.id);
        row.premium = existingReview?.programPremium || getBrPremiumSubtotal(existingReview) || '--';
    });

    return rows;
};

const BrLocationReviewGrid: React.FunctionComponent<Props> = (props) => {
    const { underwriterReviewId, readOnly } = props;

    // Global State
    const submission = useSelector(getSubmissionDetails);
    const sovLocations = useSelector(getSovLocations);
    const locations = useSelector(getSovLocations);
    const brPropertyTableValues = useSelector(getUwBRPropertyValues);
    const uwBRReview = useSelector(getUwBRReview);
    const { uwReviewSaveAllBRProperties } = useActions(Actions);
    const brPropertyTableValuesRef = useRef(brPropertyTableValues);
    const uwBRReviewRef = useRef(uwBRReview);

    // Local State
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [rowData, setRowData] = useState(buildRowData(locations, submission, brPropertyTableValues));
    const [productFilteredLocations, setProductFilteredLocations] = useState<Location[]>([]);
    const [showLocationRestoreDialog, setShowLocationRestoreDialog] = useState<boolean>(false);
    const [unavailableLocations, setUnavailableLocations] = useState<Location[]>([]);

    useEffect(() => {
        setUnavailableLocations([
            ...getProductFilteredLocations(MY_PRODUCT_LINE_TYPE, sovLocations, submission, false),
            ...getProductFilteredLocations(MY_PRODUCT_LINE_TYPE, sovLocations, submission, true).filter(
                (sl) => !locations.some((l) => l.id === sl.id)
            ),
        ]);
    }, [sovLocations, locations]);

    // Filter down to reviews for Builders Risk only
    useEffect(() => {
        setProductFilteredLocations(getProductFilteredLocations(MY_PRODUCT_LINE_TYPE, locations, submission));
    }, [locations]);

    const onGridReady = (params: any) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    };

    // Default new review values for when a review does not yet exist
    const createPropertyDefaults = (locationList: Location[]) => {
        const defaults = locationList
            .filter((location) => !location.markedAsDeleted)
            .map((location) => {
                if (
                    brPropertyTableValues &&
                    brPropertyTableValues.length > 0 &&
                    brPropertyTableValues.find((p) => p && p.locationId === location.id)
                ) {
                    return brPropertyTableValues.find((p) => p && p.locationId === location.id);
                }
                return BRPropertyModel.BuildDefaultBRPropertyModel(location);
            });
        if (defaults && defaults.length > 0) {
            uwReviewSaveAllBRProperties(defaults);
        }
    };

    useEffect(() => {
        brPropertyTableValuesRef.current = brPropertyTableValues;
    }, [brPropertyTableValues]);
    useEffect(() => {
        uwBRReviewRef.current = uwBRReview;
    }, [uwBRReview]);
    // This will be called when a location review updates the values in OTHER location reviews.
    // At time of writing, this only occurs when a TRIA percentage is changed, causing TRIA to be updated for other
    // locations.
    const onOtherLocationReviewsUpdated = useCallback(() => {
        // We need to recalculate any derived fields for all location reviews since the inputs could have changed.
        const updatedReviews: BRPropertyModel[] = brPropertyTableValuesRef.current.map((review: BRPropertyModel) => {
            const location = locations.find((l) => l.id === review.locationId);
            if (!location) return review;
            updateDerivedFieldsForBrLocationReview(
                review,
                location,
                submission,
                Number(
                    getReviewLumenParticipationPercent(
                        uwBRReviewRef.current.policyLimitOfLiability,
                        uwBRReviewRef.current.lumenLimitOfLiability
                    )
                )
            );
            return review;
        });
        uwReviewSaveAllBRProperties(updatedReviews);
    }, [locations, submission, uwBRReview, uwReviewSaveAllBRProperties]);

    useEffect(() => {
        setRowData(buildRowData(locations, submission, brPropertyTableValues));
        // TS doesn't know about agGrid's api methods
        // @ts-ignore
        gridApi?.refreshCells();
    }, [brPropertyTableValues, gridApi]);

    // Create any existing reviews, or a default review, in global state
    useEffect(() => {
        createPropertyDefaults(productFilteredLocations);
    }, [productFilteredLocations, locations]);

    const detailCellRendererParams = useMemo(() => {
        return {
            refreshStrategy: 'rows',
            readOnly: readOnly,
            detailGridOptions: {
                getRowId: (params: GetRowIdParams) => {
                    String(params.data.id);
                },
            },
            onOtherLocationReviewsUpdated: onOtherLocationReviewsUpdated,
        };
    }, []);

    return (
        <>
            <Card style={{ padding: 0 }}>
                <CardContent style={{ padding: 0 }}>
                    <div>
                        {unavailableLocations.length > 0 && !readOnly && (
                            <IconButton onClick={() => setShowLocationRestoreDialog(true)}>
                                <SettingsBackupRestoreIcon color={'primary'} fontSize={'small'} />
                            </IconButton>
                        )}
                        <LrGrid
                            columnDefs={getBrLocationReviewColumnDefs}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            rowData={rowData}
                            masterDetail={true}
                            detailCellRenderer={'detailCellRenderer'}
                            detailCellRendererParams={detailCellRendererParams}
                            suppressRowHoverHighlight={true}
                            components={{ detailCellRenderer: BrLocationReviewDetailRenderer }}
                            detailRowAutoHeight={true}
                            domLayout={'autoHeight'}
                            height={''}
                            getRowId={(params) => String(params.data.id)}
                        ></LrGrid>
                    </div>
                </CardContent>
                {showLocationRestoreDialog && (
                    <RestoreUnavailableLocationDialog
                        locations={unavailableLocations.filter((l) => !l.markedAsDeleted)}
                        noteLocations={getProductFilteredLocations(
                            MY_PRODUCT_LINE_TYPE,
                            sovLocations,
                            submission,
                            false
                        )}
                        closeDialogCallback={() => setShowLocationRestoreDialog(false)}
                        submissionId={submission.id}
                        submitRestoreRequest={(payload) => {
                            restoreLocations(
                                payload,
                                MY_PRODUCT_LINE_TYPE,
                                sovLocations,
                                submission /* DO WE NEED THIS?, doUnExcludeLocations*/
                            );
                            setShowLocationRestoreDialog(false);
                        }}
                        productLineTypeCode={MY_PRODUCT_LINE_TYPE}
                    />
                )}
            </Card>
            <Typography variant="caption" color={COLORS.GRAY_MED} style={{ float: 'right', marginTop: '0.5rem' }}>
                * underwriter adjusted premium
            </Typography>
        </>
    );
};

export default BrLocationReviewGrid;
